import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Check, ChevronLeft, ChevronRight, Eye, Sparkles } from "lucide-react";
import { FORMAT_LABEL, REFINABLE, cap, cleanOption, correctText, excerpt } from "@/components/assessment/model";

function CandidatePreview({ q, index, total, jobTitle, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="candidate-question-preview" closeTestId="candidate-question-preview-close">
        <DialogHeader>
          <DialogTitle>What candidates see</DialogTitle>
          <DialogDescription>{jobTitle} · question {index + 1} of {total}. The correct answer and scoring guidance stay hidden.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border bg-background p-5 space-y-4">
          <p className="text-[15px] leading-7 whitespace-pre-wrap [overflow-wrap:anywhere]">{q.prompt}</p>
          {q.format === "multiple_choice"
            ? <div className="space-y-2">{(q.options || []).map((o, n) => <div key={n} className="flex gap-3 rounded-lg border border-border px-4 py-3 text-sm"><span className="h-4 w-4 rounded-full border border-foreground/30 mt-0.5 shrink-0" />{cleanOption(o)}</div>)}</div>
            : <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground min-h-[120px]">Candidates type their answer here.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Options({ q, tid }) {
  const answer = correctText(q);
  return (
    <div className="space-y-2" data-testid={`question-options-${tid}`}>
      <h3 className="text-sm font-semibold">Answer options</h3>
      <ol className="hs-card divide-y divide-border overflow-hidden">
        {q.options.map((o, n) => {
          const right = o === answer;
          return (
            <li key={n} className={cn("flex gap-3 px-4 py-3 text-sm", right && "bg-primary/[0.06]")}>
              <span className="hs-mono text-xs text-muted-foreground pt-0.5">{String.fromCharCode(65 + n)}</span>
              <span className="flex-1 [overflow-wrap:anywhere]">{cleanOption(o)}</span>
              {right && <span className="text-xs text-primary font-medium shrink-0 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Correct</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function QuestionDetail({ q, index, total, onPrev, onNext, onRefine, readOnly, jobTitle }) {
  const [preview, setPreview] = useState(false);
  const mcq = q.format === "multiple_choice";
  const tid = `${q.sectionIndex}-${q.itemIndex}`;
  const chips = [`${FORMAT_LABEL[q.format] || q.format}${q.minutes ? ` · ${q.minutes} min` : ""}`, q.assesses, cap(q.difficulty)].filter(Boolean);
  return (
    <article className="space-y-5 min-w-0 motion-safe:animate-in motion-safe:fade-in" key={tid} data-testid={`assessment-question-${tid}`}>
      <div className="space-y-3">
        <div className="flex items-baseline gap-3">
          <span className="hs-mono text-sm text-muted-foreground shrink-0">{q.number}</span>
          <h2 className="font-display text-lg sm:text-xl font-semibold leading-snug [overflow-wrap:anywhere]" data-testid="question-detail-title">{excerpt(q.prompt, 90)}</h2>
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">{chips.map((t) => <span key={t} className="rounded-md bg-foreground/[0.06] px-2 py-1 text-muted-foreground">{t}</span>)}</div>
      </div>
      <div className="hs-card p-5 text-[15px] leading-7 whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`assessment-question-prompt-${tid}`}>{q.prompt}</div>
      {mcq && q.options?.length > 0 && <Options q={q} tid={tid} />}
      {q.answer_guidance && (
        <div className="space-y-2" data-testid={`question-guidance-${tid}`}>
          <h3 className="text-sm font-semibold">{mcq ? "Why this is correct" : "How to score it"}</h3>
          <p className="hs-card p-4 text-sm leading-6 text-muted-foreground whitespace-pre-wrap [overflow-wrap:anywhere]">{q.answer_guidance}</p>
        </div>
      )}
      <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 py-3 border-t border-border bg-background/95 backdrop-blur" data-testid="question-actions">
        <Button variant="outline" onClick={onRefine} disabled={readOnly || !REFINABLE.includes(q.format)} data-testid={`refine-question-${tid}`} className="gap-1.5 border-primary/40 text-primary hover:text-primary"><Sparkles className="h-4 w-4" />Refine with Copilot</Button>
        <Button variant="outline" onClick={() => setPreview(true)} data-testid={`preview-question-${tid}`} className="gap-1.5"><Eye className="h-4 w-4" />Preview as candidate</Button>
        <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
          <button onClick={onPrev} disabled={index === 0} aria-label="Previous question" data-testid="question-prev" className="ai-icon-button disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
          <span className="hs-mono w-14 text-center" data-testid="question-position">{index + 1} / {total}</span>
          <button onClick={onNext} disabled={index >= total - 1} aria-label="Next question" data-testid="question-next" className="ai-icon-button disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      {preview && <CandidatePreview q={q} index={index} total={total} jobTitle={jobTitle} onClose={() => setPreview(false)} />}
    </article>
  );
}
